import type {FC} from 'react';
import {useQuery, keepPreviousData} from '@tanstack/react-query';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import type {GridSortModel} from '@mui/x-data-grid';
import {useNavigate} from 'react-router-dom';
import {Box} from '@wellstone-solutions/web';
import {Roles} from '@wellstone-solutions/common';
import type {MembersListRoleItem, Member} from 'api/v2';
import {membersQueryOptions} from 'queries';
import {
  RowCount,
  PaginatedListGrid,
  usePaginationParams,
  DEFAULT_PAGE,
} from 'modules/datagrid';
import {ProgramSelect} from 'components/ProgramSelect';
import {useStaffListColumns} from './useStaffListColumns';

const STAFF_ROLES: MembersListRoleItem[] = [
  Roles.PEER,
  Roles.COUNSELOR,
  Roles.ADMIN,
  Roles.SUPER_ADMIN,
];

export const AllStaffTab: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const defaultSortModel: GridSortModel = [{field: 'name', sort: 'asc'}];

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    ordering,
    setOrdering,
    filters,
    setFilters,
  } = usePaginationParams({defaultSortModel});

  const query = useQuery({
    ...membersQueryOptions.list({
      page,
      pageSize,
      ordering,
      role: STAFF_ROLES,
      ...(filters?.programId && {program: filters.programId}),
    }),
    placeholderData: keepPreviousData,
  });

  const columns = useStaffListColumns({isMobile});

  const handleRowClick = ({row}: {row: Member}) => {
    if (globalThis.getSelection()?.type === 'Range') {
      return;
    }

    navigate(`/staff/${row.id}`);
  };

  return (
    <Box>
      <Box sx={{mb: 4}}>
        <ProgramSelect
          value={filters?.programId || null}
          onChange={(programId) => {
            setFilters({
              programId,
              page: DEFAULT_PAGE,
            });
          }}
        />
      </Box>

      <RowCount
        isLoading={query.isFetching}
        label="staff"
        count={query.data?.count || 0}
        pluralize={false}
      />

      <PaginatedListGrid
        testId="staff-list-datagrid"
        columns={columns}
        rows={query.data?.results || []}
        rowCount={query.data?.count || 0}
        isLoading={query.isFetching}
        page={page}
        pageSize={pageSize}
        ordering={ordering}
        isMobile={isMobile}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSortModelChange={setOrdering}
        onRowClick={handleRowClick}
      />
    </Box>
  );
};
